/* eslint-disable max-lines */
import { createSelector } from '@reduxjs/toolkit';
import {
    abProfitableTariffsFirstTouch,
    additionalTariffsList,
    desktopTariffMinSize,
    touchTariffMinSize,
} from 'reactApp/appHelpers/featuresHelpers';
import {
    aiTariffByOptionsTariffs,
    aiTariffIdVarB,
    aiTariffsVariantBTariffList,
    aiTariffVariantDTariffs,
} from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { beautifulAddressEnabled, beautifulAddressTariffList } from 'reactApp/appHelpers/featuresHelpers/features/beautifulAddress';
import { businessTariffsPromoQuota } from 'reactApp/appHelpers/featuresHelpers/features/businessTariffsPromoQuota';
import { cheapestTariff } from 'reactApp/appHelpers/featuresHelpers/features/cheapestTariff';
import { excludeTariffs } from 'reactApp/appHelpers/featuresHelpers/features/excludeTariffsByQuota';
import { excludeTariffsForFreeUsers } from 'reactApp/appHelpers/featuresHelpers/features/excludeTariffsForFreeUsers';
import { excludeTariffsMobileOverquoters } from 'reactApp/appHelpers/featuresHelpers/features/excludeTariffsForOverquoters';
import { enabledFamilyTariff, familyTariffIdFromQuery, familyTariffIds } from 'reactApp/appHelpers/featuresHelpers/features/familyTariff';
import { favorableTariffs as favorableTariffsIDs } from 'reactApp/appHelpers/featuresHelpers/features/favorableTariffs';
import { enabledExpensivePlanTrial, expensivePlanTariffIds } from 'reactApp/appHelpers/featuresHelpers/features/forceTrial';
import {
    isPromoQuotaWithLitress,
    litresTariffIds3mDiscountList,
    litresTariffIdsList,
} from 'reactApp/appHelpers/featuresHelpers/features/litres';
import { mayPromo2025YearProduct } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import { minTariffsWithAdsMobile } from 'reactApp/appHelpers/featuresHelpers/features/minTariffsWithAds';
import {
    familyByOptionsTariffs,
    familyUpsaleTariffs,
    multiTariffAdvancedTariffList,
    multiTariffAdvancedTariffListForAnonym,
    multiTariffBasicTariffList,
    multiTariffBasicTariffListForAnonym,
    multiTariffFamilyTariff,
    multiTariffFamilyTariffForAnonym,
    multiTariffFamilyTariffList,
    multiTariffFamilyTariffListEnable,
    multiTariffVigboTariffList,
} from 'reactApp/appHelpers/featuresHelpers/features/multiTariff';
import { multiTariffAdvancedOverquotaList } from 'reactApp/appHelpers/featuresHelpers/features/multiTariffOverquota';
import { isPaidUserPrices } from 'reactApp/appHelpers/featuresHelpers/features/paidUserPrices';
import {
    priceDiffAdvancedTariffListDesktop,
    priceDiffBasicTariffListDesktop,
    priceDiffBusinessTariffListDesktop,
    priceDiffFamilyTariffListDesktop,
    priceDiffTariffsMobile,
} from 'reactApp/appHelpers/featuresHelpers/features/priceDifferentiation';
import { referralProgramTariffs } from 'reactApp/appHelpers/featuresHelpers/features/referralProgram';
import { tariffOverquotaList } from 'reactApp/appHelpers/featuresHelpers/features/tariffOverquotaList';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { BillingPersonalizationSelectors } from 'reactApp/modules/billingPersonalization/billingPersonalization.selectors';
import { getBaseUserQuota } from 'reactApp/modules/family/family.selectors';
import { cheapestTariffID } from 'reactApp/modules/products/products.const';
import {
    getProductFromTariffListByQuotaAndPeriod,
    getTariffsByQuotaHelper,
    sortTariffsByYearList,
    SPACE_SIZES,
} from 'reactApp/modules/products/products.helpers';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { NormalizedTariffByProductIds, YearAndMonthProductIds } from 'reactApp/modules/products/products.types';
import { ReferralProgramSelectors } from 'reactApp/modules/referralProgram/referralProgram.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { normalizeMobileTariffList } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.helpers';
import type { ITariffsMobile } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.types';
import type { RootState } from 'reactApp/store';
import type { Product, Tariff } from 'reactApp/types/Billing';
import type { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import type { ITariffsByTab } from 'reactApp/ui/TariffSectionNew/TariffsSection.types';
import { getDiscount } from 'reactApp/utils/getDiscount';
import { getMonthes, getTariffProductsByPeriods, PERIODS } from 'reactApp/utils/Period';

import { baseYearProduct } from './TariffsPlans.helpers';

const RECOMMENDED_ID = 'W256G';
const RECOMMENDED_ID_FAVORABLE = 'W1T';

export const getMaxTariffDiscount = createSelector(
    [
        (state: RootState) => ProductsSelectors.getTariffsForQuotaLanding(state),
        (state) => ProductsSelectors.getPromoTariffs(state, true),
        (state, isMobile) => isMobile,
        // tempexp_16373-next-line
        (state) => ProductsSelectors.getFavorableTariffList(state),
        (state) => ProductsSelectors.getPreferredTariffs(state),
        // tempexp_18086-start
        (state: RootState) => ReferralProgramSelectors.getRefId(state),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(referralProgramTariffs?.month || []),
                ...(referralProgramTariffs?.year || []),
            ]),
        // tempexp_18086-end
    ],
    // tempexp_16373-next-line
    (
        nonSpecialTariffs,
        promoTariffs,
        isMobile,
        favorableTariffs,
        preferredTariffs,
        // tempexp_18086-start
        ref_id,
        referralTariffs
        // tempexp_18086-end
    ) => {
        let tariffs: Tariff[] = promoTariffs?.length ? promoTariffs : nonSpecialTariffs;

        const minTariffSize = SPACE_SIZES[`_${isMobile ? touchTariffMinSize : desktopTariffMinSize}`];
        tariffs = tariffs.filter((tariff) => tariff?.space?.original >= minTariffSize);

        if (favorableTariffs.length) {
            tariffs = favorableTariffs;
        }

        if (preferredTariffs.length) {
            tariffs = preferredTariffs;
        }

        // tempexp_17380-next-line
        if (isPaidUserPrices) {
            tariffs = nonSpecialTariffs;
        }

        let discounts = tariffs.map((tariffItem) => {
            const { monthProduct, yearProduct } = getTariffProductsByPeriods(tariffItem.products);

            if (!(monthProduct && yearProduct)) {
                return 0;
            }

            const yearPriceByMonth = getMonthes(yearProduct.period) * monthProduct.price;
            const { discount } = getDiscount({
                discountPrice: yearProduct?.discountPrice || yearProduct.price,
                price: yearPriceByMonth,
            });

            return discount;
        });
        // tempexp_18086-start
        if (ref_id && referralTariffs) {
            discounts = tariffs.map((tariff) => {
                const { monthProduct } = referralTariffs[tariff.space.original] || {};
                const { discount } = getDiscount({
                    discountPrice: monthProduct?.discountPrice || 0,
                    price: monthProduct?.price || 0,
                });
                return discount;
            });
        }
        // tempexp_18086-end
        return discounts.length && Math.max(...discounts);
    }
);

// tempexp_19495-start
export const getOverquotaTariffsList = createSelector(
    [
        (state: RootState, isMobile) => isMobile,
        (state: RootState) => {
            const list: YearAndMonthProductIds = {
                year: tariffOverquotaList?.year || multiTariffAdvancedTariffList?.year, // не воровство, а фоллбек!
                month: tariffOverquotaList?.month || multiTariffAdvancedTariffList?.month,
            };

            return ProductsSelectors.getNormalizedTariffsListByProductIds(state, list, 'overquota_sale');
        },
    ],
    (isMobile, overquotaTariffs): ITariffsByTab[] | ITariffsMobile[] => {
        if (!isMobile) {
            return overquotaTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
                acc.push({
                    space: tariffItem.space,
                    tariffId: tariffItem.tariffId,
                    isRecommended: tariffItem.tariffId.includes(RECOMMENDED_ID_FAVORABLE),
                    products: tariffItem.products,
                });
                return acc;
            }, []);
        }
        const mobileTariffList = overquotaTariffs.reduce((acc, item) => {
            const monthTariffs: [Product | undefined, Product | undefined] = [item.products.monthProduct, undefined];
            acc[item.products.yearProduct.id] = [item.products.yearProduct, ...monthTariffs];
            return acc;
        }, <Record<string, [Product, Product | undefined, Product | undefined]>>{});

        return normalizeMobileTariffList(mobileTariffList);
    }
);
// tempexp_19495-end

export const getTariffsList = createSelector(
    [
        (state: RootState) => ProductsSelectors.getTariffsForQuotaLanding(state),
        (state: RootState) => ProductsSelectors.getPromoTariffs(state, true),
        // tempexp_16481-next-line
        (state: RootState, isMobile) => isMobile,
        (state: RootState, _, minTariff?: number) => minTariff,
        (state: RootState, _, __, forceFamilyTariffFromQuery?: boolean) => forceFamilyTariffFromQuery,
        (state: RootState, _, __, ___, mayPromo2025Enabled?: boolean) => mayPromo2025Enabled,
        (state: RootState) => getBaseUserQuota(state),
        // tempexp_16373-next-line
        (state: RootState) => ProductsSelectors.getFavorableTariffList(state),
        (state: RootState) => ProductsSelectors.getNormalizedProductsBySpace(state, additionalTariffsList?.tariffs || []),
        (state: RootState) => ProductsSelectors.getPreferredTariffs(state),
        (state: RootState) => ProductsSelectors.getThreeMonthPeriodTariffs(state),
        (state: RootState) => ProductsSelectors.getAvailableProductsByListIds(state, abProfitableTariffsFirstTouch.tariffs),
        (state: RootState) => ProductsSelectors.getCheapestProducts(state),
        (state: RootState) => UserSelectors.isPaidUser(state),
        // tempexp-17509-start
        (state: RootState, isMobile) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, isMobile ? litresTariffIdsList : litresTariffIds3mDiscountList),
        // tempexp-17509-end
        // tempexp_18795-next-line
        (state: RootState) => UserSelectors.isAlmostFullFreeUser(state),
        // tempexp_18086-start
        (state: RootState) => ReferralProgramSelectors.getRefId(state),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(referralProgramTariffs?.month || []),
                ...(referralProgramTariffs?.year || []),
            ]),
        // tempexp_18086-end
        (state: RootState) => ProductsSelectors.getTariffById(state, familyTariffIds.name),
        (state: RootState) => ProductsSelectors.getProductById(state, familyTariffIds.month),
        (state: RootState) => ProductsSelectors.getTariffById(state, familyTariffIdFromQuery),
        (state: RootState) => UserSelectors.isOverQuotaUser(state),
        (state: RootState) => ProductsSelectors.getMayPromo2025Tariff(state),
        (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(
                state,
                { year: [expensivePlanTariffIds.year], month: [expensivePlanTariffIds.month] },
                'expensive_landing'
            ),
    ],
    (
        nonSpecialTariffs,
        promoTariffs,
        isMobile,
        minTariff,
        forceFamilyTariffFromQuery,
        mayPromo2025Enabled,
        baseQuota,
        // tempexp_16373-next-line
        favorableTariffs,
        additionalTariffs,
        preferredTariffs,
        threeMonthPeriodTariffs,
        touchProfitableTariffs,
        cheapestProducts,
        isPaidUser,
        // tempexp-17509-next-line
        litressTariffs,
        // tempexp_18795-next-line
        isAlmostFullFreeUser,
        // tempexp_18086-start
        ref_id,
        refferalTariffs,
        // tempexp_18086-end
        familyTariffsLoaded,
        familyMonthProduct,
        familyTariffsFromQuery,
        isOverQuota,
        mayPromo2025Tariff,
        expensivePlanTariffs
    ): ITariffsByTab[] | ITariffsMobile[] => {
        let tariffs: Tariff[] = [];

        // В задаче CLOUDWEB-17222 в варианте 'c' базовые годовые тарифы без скидки, поэтому промо тарифы не показываем
        tariffs = promoTariffs?.length && additionalTariffsList?.variant !== 'c' ? promoTariffs : nonSpecialTariffs;

        const prettyAddressTariffs = tariffs;

        const showFamilyTariff = !ref_id && (enabledFamilyTariff || forceFamilyTariffFromQuery);
        const familyTariffs = forceFamilyTariffFromQuery ? familyTariffsFromQuery : familyTariffsLoaded;
        const familyTariffId = forceFamilyTariffFromQuery ? familyTariffIdFromQuery : familyTariffIds.name;

        const expensivePlanTariff = expensivePlanTariffs[0];

        // tempexp_16373-next-line
        if (favorableTariffs.length) {
            tariffs = favorableTariffs;
        }

        if (preferredTariffs.length) {
            tariffs = preferredTariffs;
        } else if (threeMonthPeriodTariffs.length) {
            tariffs = tariffs.map((tariff) => {
                const threeMonthPeriodProduct = getProductFromTariffListByQuotaAndPeriod(
                    threeMonthPeriodTariffs,
                    tariff.space.original,
                    PERIODS.threeMonths
                );
                const products = [...tariff.products];
                if (threeMonthPeriodProduct) {
                    products.push(threeMonthPeriodProduct);
                }

                return {
                    ...tariff,
                    products,
                };
            });
        }

        if (beautifulAddressEnabled && prettyAddressTariffs.length) {
            tariffs = prettyAddressTariffs.reverse();
        }

        if (mayPromo2025Enabled && mayPromo2025Tariff) {
            const index = tariffs.findIndex((tariff) => tariff.space.original === SPACE_LIST.tb_1);
            if (index !== -1) {
                tariffs.splice(index, 1);
            }
            tariffs.splice(1, 0, mayPromo2025Tariff);
        }

        if (showFamilyTariff && familyTariffs) {
            tariffs.push(familyTariffs);
        }

        let list = tariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
            const products = getTariffProductsByPeriods(tariffItem.products);

            let familyTariff;
            if (showFamilyTariff && tariffItem.id === familyTariffId) {
                familyTariff = getTariffProductsByPeriods(tariffItem.products);
            }

            // const additionalTariff = !cyberMonday2025 ? additionalTariffs[tariffItem.space.original] : undefined;
            const additionalTariff = additionalTariffs[tariffItem.space.original];

            // tempexp-17509-start
            const litresTariff = isPromoQuotaWithLitress && !isPaidUser ? litressTariffs[tariffItem.space.original] : undefined;
            // tempexp-17509-end

            // tempexp_18086-start
            const referralProducts = ref_id ? refferalTariffs[tariffItem.space.original] : undefined;
            // tempexp_18086-end

            if (products.yearProduct) {
                let recommendedId = RECOMMENDED_ID;

                if (favorableTariffsIDs.length) {
                    recommendedId = RECOMMENDED_ID_FAVORABLE;
                }

                const yearProduct =
                    // tempexp-18086-next-line
                    referralProducts?.yearProduct || // !!! Если продукты реферальной программы доступны, они первые в списке
                    litresTariff?.yearProduct ||
                    additionalTariff?.yearProduct ||
                    products.yearProduct;
                const monthProduct =
                    // tempexp-18086-next-line
                    referralProducts?.monthProduct ||
                    (familyTariff && (familyMonthProduct || familyTariff?.monthProduct)) ||
                    (mayPromo2025Enabled && mayPromo2025Tariff && tariffItem.id === mayPromo2025YearProduct && products.monthProduct) ||
                    litresTariff?.monthProduct ||
                    additionalTariff?.monthProduct ||
                    products.monthProduct;
                const threeMonthProduct = products.threeMonthProduct;

                let recommendedTariffId = favorableTariffsIDs.length ? RECOMMENDED_ID_FAVORABLE : recommendedId;

                if (showFamilyTariff && !enabledExpensivePlanTrial) {
                    recommendedTariffId = familyTariffId;
                }

                if (enabledExpensivePlanTrial && !ref_id) {
                    recommendedTariffId = expensivePlanTariff?.tariffId;
                }

                let isRecommended = cheapestTariff === 'a' || isPaidUser ? tariffItem.id.includes(recommendedTariffId) : false;

                if (mayPromo2025Enabled && mayPromo2025Tariff) {
                    isRecommended = tariffItem.id === mayPromo2025Tariff.id;
                }

                acc.push({
                    space: tariffItem.space,
                    tariffId: tariffItem.id,
                    // tempexp_16373-next-line
                    isRecommended,
                    products: preferredTariffs.length
                        ? products
                        : {
                              yearProduct,
                              monthProduct,
                              threeMonthProduct,
                          },
                });
            }

            return acc;
        }, []);

        if (enabledExpensivePlanTrial && expensivePlanTariff && !ref_id) {
            list[0] = { ...expensivePlanTariff, isRecommended: true };
        }

        // tempexp_16373-next-line
        if (!favorableTariffsIDs.length) {
            list.unshift({
                tariffId: 'base',
                space: { ...baseQuota },
                isRecommended: false,
                isBase: true,
                products: {
                    yearProduct: {
                        ...baseYearProduct,
                        space: baseQuota,
                    },
                    monthProduct: undefined,
                },
            });
        }

        const minTariffParam = minTariff && minTariff > 0 ? minTariff : isMobile ? touchTariffMinSize : desktopTariffMinSize;
        const minTariffSize = SPACE_SIZES[`_${minTariffParam}`];

        if (minTariffSize > 0) {
            list = list.filter((tariff) => tariff?.space?.original >= minTariffSize);
        }

        if (!isMobile && cheapestProducts.yearProduct && cheapestProducts.monthProduct && !isPaidUser) {
            const cheapestTariffTabOptions = {
                tariffId: cheapestTariffID,
                space: { ...baseQuota }, // Так как квота 1Кб, то используется базовый тариф
                isRecommended: true,
                isCheapest: true,
                products: cheapestProducts,
            };
            if (cheapestTariff === 'b') {
                list.unshift(cheapestTariffTabOptions);
            }
            if (cheapestTariff === 'c') {
                list.push(cheapestTariffTabOptions);
            }
        }

        if (showFamilyTariff) {
            const lastElemList = list.pop();
            if (lastElemList) {
                list.splice(1, 0, lastElemList);
            }
        }

        if (isMobile) {
            // tempexp-16876-start
            // список id годовых продуктов
            const profitableTariffs = abProfitableTariffsFirstTouch.tariffs;

            const sortedTouchProfitableTariffs = profitableTariffs.map((productYearId) =>
                touchProfitableTariffs.find((tariff) => tariff.products.some((product) => product.id === productYearId))
            );

            // tempexp_17242-next-line
            if (profitableTariffs?.length && !preferredTariffs?.length) {
                const sortedList: ITariffsByTab[] = [];
                sortedTouchProfitableTariffs.forEach((tariff) => {
                    const foundItem = list.find((t) => t.tariffId === tariff?.id);
                    if (foundItem) {
                        sortedList.push(foundItem);
                    }
                });

                list = sortedList;

                // tempexp_17221-next-line: Исключаем тарифы которых нет в массиве из фичи.
                list = list.filter((item) => profitableTariffs.includes(item.products.yearProduct.id));
            }
            // tempexp-16876-end

            const mobileTariffList = list.reduce((acc, item) => {
                // не показываем базовый тариф на таче
                if (item.isBase) {
                    return acc;
                }

                // tempexp_18795-start
                // если тариф в списке на исключение, не добавляем его
                if (isAlmostFullFreeUser && excludeTariffsForFreeUsers?.quotas?.includes(item.space.original)) {
                    return acc;
                }
                // tempexp_18795-end

                if (isOverQuota && excludeTariffsMobileOverquoters?.quotas?.includes(item.space.original)) {
                    return acc;
                }

                // tempexp_18796-start
                const excludeMonth = excludeTariffs && excludeTariffs.month?.some((quota) => quota === item.space.original);

                const monthTariffs: [Product | undefined, Product | undefined] = excludeMonth
                    ? [undefined, undefined]
                    : [item.products.monthProduct, item.products.threeMonthProduct];
                // tempexp_18796-end

                acc[item.products.yearProduct.id] = [item.products.yearProduct, ...monthTariffs];

                return acc;
            }, <Record<string, [Product, Product | undefined, Product | undefined]>>{});

            return normalizeMobileTariffList(mobileTariffList, Boolean(promoTariffs?.length));
        }

        return list;
    }
);

// tempexp_17380-start
export const getTariffListPaidUsers = createSelector(
    [
        (state: RootState) => ProductsSelectors.getTariffsForQuotaLanding(state),
        // tempexp_16373-next-line
        (state: RootState) => ProductsSelectors.getFavorableTariffList(state),
    ],
    (nonSpecialTariffs, favorableTariffs): ITariffsByTab[] => {
        const items: ITariffsByTab[] = [];

        for (const tariff of favorableTariffs) {
            const item = getTariffsByQuotaHelper(nonSpecialTariffs, tariff.space.original)[0];
            const products = getTariffProductsByPeriods(item.products);
            const isRecommended = item.id.includes(RECOMMENDED_ID_FAVORABLE);

            items.push({
                space: item.space,
                tariffId: item.id,
                isRecommended,
                products,
            });
        }

        return items;
    }
);
// tempexp_17380-end

// tempexp_17815-start
export const getTariffListPaidUsersMobile = createSelector(
    [(state: RootState) => ProductsSelectors.getTariffsForQuotaLanding(state)],
    (nonSpecialTariffs): ITariffsMobile[] => {
        const mobileTariffList = nonSpecialTariffs.reduce((acc, item) => {
            const products = getTariffProductsByPeriods(item.products);

            if (item.space.original < SPACE_LIST.gb_64) {
                return acc;
            }

            acc[item.space.original] = [products.yearProduct, products.monthProduct];
            return acc;
        }, <Record<number, [Product, Product | undefined]>>{});

        return normalizeMobileTariffList(mobileTariffList, false);
    }
);
// tempexp_17815-end

const advancedMultitariffPostfix = 'landing-segmentation-advanced';
const basicMultitariffPostfix = 'landing-segmentation-basic';
const vigboMultiTariffPostfix = 'landing-segmentation-vigbo';
const familyMultiTariffPostfix = 'landing-segmentation-family';
const businessMultiTariffPostfix = 'landing-segmentation-business';

/**
 * Общая логика для мультириф-селекторов на декстопе
 * !IMPORTANT. Не расширяем оригинальный селектор. Если для эксперимента нужны другие тарифы/цены - заводим отдельный
 * !исключение: если добавляется новый таб
 * см как сделано getMultiTariffListOverquota и getMultiTariffListOverquota
 */
const computeMultiTariffListData = (
    {
        advancedTariffs,
        basicTariffs,
        familyTariff,
        vigboTariffs,
        businessTariffs,
        recommendedProductIds,
        listIdsForSorting,
    }: {
        advancedTariffs: NormalizedTariffByProductIds[];
        basicTariffs: NormalizedTariffByProductIds[];
        familyTariff?: Tariff | NormalizedTariffByProductIds[];
        vigboTariffs?: NormalizedTariffByProductIds[];
        businessTariffs?: NormalizedTariffByProductIds[];
        recommendedProductIds?: Partial<Record<ETabsName, string | undefined>>;
        listIdsForSorting?: Partial<Record<ETabsName, string[]>>;
    },
    enableListSorting: boolean = false
) => {
    const basicList = basicTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
        if (tariffItem.products.yearProduct && tariffItem.products.monthProduct) {
            const isRecommended = recommendedProductIds?.basic
                ? tariffItem.products.yearProduct.id === recommendedProductIds.basic
                : ['W1TB-landing-segmentation-basic'].includes(tariffItem.tariffId);

            acc.push({
                space: tariffItem.space,
                tariffId: tariffItem.tariffId,
                isRecommended,
                isMultiBasic: true,
                products: tariffItem.products,
            });
        }

        return acc;
    }, []);

    const advancedList = advancedTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
        if (tariffItem.products.yearProduct) {
            const isRecommended = recommendedProductIds?.advanced
                ? tariffItem.products.yearProduct.id === recommendedProductIds.advanced
                : ['W1TB-landing-segmentation-advanced'].includes(tariffItem.tariffId);

            acc.push({
                space: tariffItem.space,
                tariffId: tariffItem.tariffId,
                isRecommended,
                isMultiAdvanced: true,
                products: tariffItem.products,
            });
        }

        return acc;
    }, []);

    let family;

    if (familyTariff) {
        if (Array.isArray(familyTariff)) {
            family = familyTariff
                .reduce((acc: ITariffsByTab[], tariffItem) => {
                    if (tariffItem.products.yearProduct) {
                        const isRecommended = recommendedProductIds?.advanced
                            ? tariffItem.products.yearProduct.id === recommendedProductIds.family
                            : ['W1TB-landing-segmentation-family'].includes(tariffItem.tariffId);

                        acc.push({
                            space: tariffItem.space,
                            tariffId: tariffItem.tariffId,
                            isRecommended,
                            isFamilyTariff: true,
                            products: tariffItem.products,
                        });
                    }

                    return acc;
                }, [])
                .reverse();
        } else {
            const familyProducts = getTariffProductsByPeriods(familyTariff.products);

            family = {
                space: familyTariff.space,
                tariffId: familyTariff.id,
                products: familyProducts,
                isFamilyTariff: true,
            };
        }
    }

    let vigbo;

    if (vigboTariffs?.length) {
        vigbo = vigboTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
            if (tariffItem.products.yearProduct) {
                acc.push({
                    space: tariffItem.space,
                    tariffId: tariffItem.tariffId,
                    isRecommended: ['W1TB-landing-segmentation-vigbo'].includes(tariffItem.tariffId),
                    isVigboTariff: true,
                    products: tariffItem.products,
                });
            }

            return acc;
        }, []);
    }

    let forWork;

    if (businessTariffs?.length) {
        forWork = businessTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
            if (tariffItem.products.yearProduct && tariffItem.products.monthProduct) {
                const tariffByTab = {
                    space: tariffItem.space,
                    tariffId: tariffItem.tariffId,
                    isRecommended: [`W1TB-${businessMultiTariffPostfix}`].includes(tariffItem.tariffId),
                    isForWorkTariff: true,
                    products: tariffItem.products,
                };
                if (tariffItem.space.original === SPACE_LIST.tb_4) {
                    acc.unshift(tariffByTab);
                } else {
                    acc.push(tariffByTab);
                }
            }

            return acc;
        }, []);
    }

    if (enableListSorting) {
        const familyList = Array.isArray(family)
            ? sortTariffsByYearList(family, listIdsForSorting?.family || multiTariffFamilyTariffList?.year || [])
            : family;
        const forWorkList = Array.isArray(forWork)
            ? sortTariffsByYearList(forWork, listIdsForSorting?.for_work || businessTariffsPromoQuota?.tariffs?.year || [])
            : forWork;

        return {
            basic: sortTariffsByYearList(basicList, listIdsForSorting?.basic || multiTariffBasicTariffList?.year || []),
            advanced: sortTariffsByYearList(advancedList, listIdsForSorting?.advanced || multiTariffAdvancedTariffList?.year || []),
            family: familyList || [],
            vigbo: vigbo ? sortTariffsByYearList(vigbo, listIdsForSorting?.vigbo || multiTariffVigboTariffList?.year || []) : [],
            for_work: forWorkList as ITariffsByTab[],
            load: true,
        };
    }

    return {
        basic: basicList.reverse(),
        advanced: advancedList.reverse(),
        family: family || [],
        vigbo: vigbo ? vigbo.reverse() : [],
        for_work: (forWork ? forWork.reverse() : []) as ITariffsByTab[],
        load: true,
    };
};

type MultiTariffSelector = (state: RootState) => NormalizedTariffByProductIds[];
type MultiTariffRecommendedProductIdsSelector = (state: RootState) => Partial<Record<ETabsName, string | undefined>>;
type MultiTariffListIdsForSortingSelector = (state: RootState) => Partial<Record<ETabsName, string[]>>;
type FamilyMultiTariffSelector = (state: RootState) => undefined | Tariff | NormalizedTariffByProductIds[];

const createMultiTariffSelectors = ({
    basicTariffs = (state: RootState) =>
        ProductsSelectors.getNormalizedTariffsListByProductIds(state, multiTariffBasicTariffList, basicMultitariffPostfix),
    advancedTariffs = (state: RootState) =>
        ProductsSelectors.getNormalizedTariffsListByProductIds(state, multiTariffAdvancedTariffList, advancedMultitariffPostfix),
    familyTariffs = (state: RootState) => ProductsSelectors.getTariffById(state, multiTariffFamilyTariff),
    vigboTariffs = (state: RootState) =>
        ProductsSelectors.getNormalizedTariffsListByProductIds(state, multiTariffVigboTariffList, vigboMultiTariffPostfix),
    businessTariffs = (state: RootState) =>
        ProductsSelectors.getNormalizedTariffsListByProductIds(state, businessTariffsPromoQuota.tariffs, businessMultiTariffPostfix),
    recommendedProductIds,
    listIdsForSorting,
}: {
    basicTariffs?: MultiTariffSelector;
    advancedTariffs?: MultiTariffSelector;
    familyTariffs?: FamilyMultiTariffSelector;
    vigboTariffs?: MultiTariffSelector;
    businessTariffs?: MultiTariffSelector;
    recommendedProductIds?: MultiTariffRecommendedProductIdsSelector;
    listIdsForSorting?: MultiTariffListIdsForSortingSelector;
} = {}) => {
    return (state: RootState): Parameters<typeof computeMultiTariffListData>[0] => ({
        basicTariffs: basicTariffs?.(state),
        advancedTariffs: advancedTariffs?.(state),
        familyTariff: familyTariffs?.(state),
        vigboTariffs: vigboTariffs?.(state),
        businessTariffs: businessTariffs?.(state),
        recommendedProductIds: recommendedProductIds?.(state),
        listIdsForSorting: listIdsForSorting?.(state),
    });
};

export const getMultiTariffList = createSelector(
    [
        createMultiTariffSelectors({
            familyTariffs: (state: RootState) =>
                multiTariffFamilyTariffListEnable
                    ? ProductsSelectors.getNormalizedTariffsListByProductIds(state, multiTariffFamilyTariffList, familyMultiTariffPostfix)
                    : ProductsSelectors.getTariffById(state, multiTariffFamilyTariff),
        }),
        (_, enableListSorting: boolean) => enableListSorting,
    ],
    (state, enableListSorting) => computeMultiTariffListData(state, enableListSorting)
);

export const getMultiTariffAnonymList = createSelector(
    createMultiTariffSelectors({
        advancedTariffs: (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(
                state,
                multiTariffAdvancedTariffListForAnonym,
                advancedMultitariffPostfix
            ),
        basicTariffs: (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(state, multiTariffBasicTariffListForAnonym, basicMultitariffPostfix),
        familyTariffs: (state: RootState) => ProductsSelectors.getTariffById(state, multiTariffFamilyTariffForAnonym),
    }),
    computeMultiTariffListData
);

// tempexp_19411-start - Повышаем для оверквотников цены
export const getMultiTariffListOverquota = createSelector(
    createMultiTariffSelectors({
        advancedTariffs: (state: RootState) => {
            const list = {
                year: multiTariffAdvancedOverquotaList?.year || multiTariffAdvancedTariffList.year,
                month: multiTariffAdvancedOverquotaList?.month || multiTariffAdvancedTariffList.month,
            };
            return ProductsSelectors.getNormalizedTariffsListByProductIds(state, list, advancedMultitariffPostfix);
        },
    }),
    computeMultiTariffListData
);
// tempexp_19411-end

// tempexp_19870-start
export const getMultiTariffListPriceDiff = createSelector(
    createMultiTariffSelectors({
        advancedTariffs: (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(state, priceDiffAdvancedTariffListDesktop, advancedMultitariffPostfix),
        basicTariffs: (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(state, priceDiffBasicTariffListDesktop, basicMultitariffPostfix),
        familyTariffs: (state: RootState) => {
            const tariff = ProductsSelectors.getTariffById(state, multiTariffFamilyTariff);
            if (tariff) {
                const { monthProduct, yearProduct } = getTariffProductsByPeriods(tariff.products);
                const newMonthProductId = priceDiffFamilyTariffListDesktop?.month?.[0];
                const newMonthProduct = ProductsSelectors.getProductById(state, newMonthProductId);
                const newYearProductId = priceDiffFamilyTariffListDesktop?.year?.[0];
                const newYearProduct = ProductsSelectors.getProductById(state, newYearProductId);

                return {
                    ...tariff,
                    products: [newMonthProduct || monthProduct, newYearProduct || yearProduct],
                };
            }
        },
        businessTariffs: (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(state, priceDiffBusinessTariffListDesktop, businessMultiTariffPostfix),
    }),
    computeMultiTariffListData
);
// tempexp_19870-end

// tempexp_19373-start
export const getMultiTariffListBeautifulAddress = createSelector(
    createMultiTariffSelectors({
        advancedTariffs: (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(state, beautifulAddressTariffList, advancedMultitariffPostfix),
    }),
    computeMultiTariffListData
);
// tempexp_19373-end

// tempexp_19645-start
export const getMultiTariffListAiTariff = createSelector(
    [
        (state: RootState) =>
            createMultiTariffSelectors({
                advancedTariffs: (state: RootState) =>
                    ProductsSelectors.getNormalizedTariffsListByProductIds(state, aiTariffsVariantBTariffList, advancedMultitariffPostfix),
            })(state),
        (state: RootState) => ProductsSelectors.getNormalizedTariffsListByProductIds(state, aiTariffIdVarB, 'family-features'),
    ],
    ({ advancedTariffs, basicTariffs, familyTariff, vigboTariffs, businessTariffs }, additional?: NormalizedTariffByProductIds[]) => {
        if (advancedTariffs?.length && additional?.length) {
            const res = computeMultiTariffListData({
                advancedTariffs: [...advancedTariffs, ...additional],
                basicTariffs,
                familyTariff,
                vigboTariffs,
                businessTariffs,
            });

            if (res.advanced?.[0]) {
                res.advanced[0].isAiTariff = true;
            }

            return res;
        }

        return computeMultiTariffListData({ advancedTariffs, basicTariffs, familyTariff, vigboTariffs, businessTariffs });
    }
);
export const getAiTariffsByOptionTariffList = createSelector(
    [(state: RootState) => ProductsSelectors.getNormalizedTariffsListByProductIds(state, aiTariffByOptionsTariffs, 'ai-tariff-by-options')],
    (tariffs) => {
        return tariffs
            .reduce((acc: ITariffsByTab[], tariffItem) => {
                if (tariffItem.products.yearProduct) {
                    acc.push({
                        space: tariffItem.space,
                        tariffId: tariffItem.tariffId,
                        products: tariffItem.products,
                    });
                }

                return acc;
            }, [])
            .reverse();
    }
);
export const getMultiTariffListAiTariffVariantD = createSelector(
    createMultiTariffSelectors({
        advancedTariffs: (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(state, aiTariffVariantDTariffs, 'ai-tariff-variant-d'),
    }),
    computeMultiTariffListData
);
// tempexp_19645-end

export const getFamilyByOptionTariffList = createSelector(
    [(state: RootState) => ProductsSelectors.getNormalizedTariffsListByProductIds(state, familyByOptionsTariffs, 'family-by-options')],
    (familyTariffs) => {
        return familyTariffs
            .reduce((acc: ITariffsByTab[], tariffItem) => {
                if (tariffItem.products.yearProduct) {
                    acc.push({
                        space: tariffItem.space,
                        tariffId: tariffItem.tariffId,
                        products: tariffItem.products,
                    });
                }

                return acc;
            }, [])
            .reverse();
    }
);

export const getFamilyUpsaleTariffs = createSelector(
    [(state: RootState) => ProductsSelectors.getNormalizedTariffsListByProductIds(state, familyUpsaleTariffs, 'family-upsale')],
    (familyTariffs) => {
        return familyTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
            acc.push({
                space: tariffItem.space,
                tariffId: tariffItem.tariffId,
                products: tariffItem.products,
            });

            return acc;
        }, []);
    }
);

// tempexp_19870-start
export const getPriceDiffTariffsMobile = createSelector(
    [
        (state: RootState) => ProductsSelectors.getAvailableProductsByListIds(state, priceDiffTariffsMobile.month || []),
        (state: RootState) => ProductsSelectors.getAvailableProductsByListIds(state, priceDiffTariffsMobile.year || []),
    ],
    (monthTariffs, yearTariffs): ITariffsMobile[] => {
        const tariffs: { monthProduct: Product; yearProduct: Product }[] = [];

        const priceDiffTariffsMobileMonth = priceDiffTariffsMobile.month || [];
        const priceDiffTariffsMobileYear = priceDiffTariffsMobile.year || [];

        if (!monthTariffs.length && !yearTariffs.length) {
            return [];
        }

        const monthProducts = monthTariffs
            .map((item) => {
                const { monthProduct } = getTariffProductsByPeriods(item.products);
                return monthProduct;
            })
            .sort((a, b) => {
                return priceDiffTariffsMobileMonth.indexOf(a.id) - priceDiffTariffsMobileMonth.indexOf(b.id);
            });

        const yearProducts = yearTariffs
            .map((item) => {
                const { yearProduct } = getTariffProductsByPeriods(item.products);
                return yearProduct;
            })
            .sort((a, b) => {
                return priceDiffTariffsMobileYear.indexOf(a.id) - priceDiffTariffsMobileYear.indexOf(b.id);
            });

        for (let i = 0; i < priceDiffTariffsMobileYear.length; i++) {
            const yearProduct = yearProducts[i];
            const monthProduct = monthProducts[i];

            tariffs.push({
                yearProduct,
                monthProduct,
            });
        }

        const mobileTariffList = tariffs.reduce((acc, item) => {
            const monthTariffs: [Product | undefined, Product | undefined] = [item.monthProduct, undefined];
            acc[item.yearProduct.id] = [item.yearProduct, ...monthTariffs];
            return acc;
        }, <Record<string, [Product, Product | undefined, Product | undefined]>>{});

        return normalizeMobileTariffList(mobileTariffList);
    }
);
// tempexp_19870-end

// tempexp_19902-start
export const getMinTariffsWithAdsMobile = createSelector(
    [
        (state: RootState) => ProductsSelectors.getAvailableProductsByListIds(state, minTariffsWithAdsMobile?.month || []),
        (state: RootState) => ProductsSelectors.getAvailableProductsByListIds(state, minTariffsWithAdsMobile?.year || []),
    ],
    (monthTariffs, yearTariffs): ITariffsMobile[] => {
        const tariffs: { monthProduct: Product; yearProduct: Product }[] = [];

        const minTariffsWithAdsMobileMonth = minTariffsWithAdsMobile?.month || [];
        const minTariffsWithAdsMobileYear = minTariffsWithAdsMobile?.year || [];

        if (!monthTariffs.length && !yearTariffs.length) {
            return [];
        }

        const monthProducts = monthTariffs
            .map((item) => {
                const { monthProduct } = getTariffProductsByPeriods(item.products);
                return monthProduct;
            })
            .sort((a, b) => {
                return minTariffsWithAdsMobileMonth.indexOf(a.id) - minTariffsWithAdsMobileMonth.indexOf(b.id);
            });

        const yearProducts = yearTariffs
            .map((item) => {
                const { yearProduct } = getTariffProductsByPeriods(item.products);
                return yearProduct;
            })
            .sort((a, b) => {
                return minTariffsWithAdsMobileYear.indexOf(a.id) - minTariffsWithAdsMobileYear.indexOf(b.id);
            });

        for (let i = 0; i < minTariffsWithAdsMobileYear.length; i++) {
            const yearProduct = yearProducts[i];
            const monthProduct = monthProducts[i];

            tariffs.push({
                yearProduct,
                monthProduct,
            });
        }

        const mobileTariffList = tariffs.reduce((acc, item) => {
            const monthTariffs: [Product | undefined, Product | undefined] = [item.monthProduct, undefined];
            acc[item.yearProduct.id] = [item.yearProduct, ...monthTariffs];
            return acc;
        }, <Record<string, [Product, Product | undefined, Product | undefined]>>{});

        return normalizeMobileTariffList(mobileTariffList);
    }
);
// tempexp_19902-end

// tempexp_19915-start
export const getMultiTariffBillingPersonalization = createSelector(
    createMultiTariffSelectors({
        advancedTariffs: (state: RootState) => {
            const tariffs = BillingPersonalizationSelectors.getTariffIDsDesktop(state).advanced;
            return ProductsSelectors.getNormalizedTariffsListByProductIds(state, tariffs, advancedMultitariffPostfix);
        },
        basicTariffs: (state: RootState) => {
            const tariffs = BillingPersonalizationSelectors.getTariffIDsDesktop(state).basic;
            return ProductsSelectors.getNormalizedTariffsListByProductIds(state, tariffs, basicMultitariffPostfix);
        },
        familyTariffs: (state: RootState) => {
            const tariffs = BillingPersonalizationSelectors.getTariffIDsDesktop(state).family;
            return ProductsSelectors.getNormalizedTariffsListByProductIds(state, tariffs, familyMultiTariffPostfix);
        },
        businessTariffs: (state: RootState) => {
            const tariffs = BillingPersonalizationSelectors.getTariffIDsDesktop(state).for_work;
            return ProductsSelectors.getNormalizedTariffsListByProductIds(state, tariffs, businessMultiTariffPostfix);
        },
        recommendedProductIds: (state: RootState) => BillingPersonalizationSelectors.getDefaultProductIds(state),
        listIdsForSorting: (state: RootState) => BillingPersonalizationSelectors.getYearTariffIDsDesktop(state),
    }),
    (state) => computeMultiTariffListData(state, true)
);
// tempexp_19915-end
